import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { catchError, EMPTY, Observable, switchMap, throwError } from 'rxjs';
import { API_BASE_URL } from '@/app/core/auto-generated/api.service';
import { AuthRedirectService } from './auth-redirect.service';

const LOGIN_REQUIRED_ERRORS = new Set([
  'consent_required',
  'invalid_grant',
  'login_required',
  'missing_refresh_token',
  'missing_token',
]);

/**
 * Recovers API requests when the server rejects a token that Auth0 still
 * considered usable. Force-refreshes once, retries with the new token, then
 * redirects to login if the session cannot be recovered.
 */
export const authRecoveryInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const apiBaseUrl = inject(API_BASE_URL);
  const auth = inject(AuthService);
  const authRedirect = inject(AuthRedirectService);

  if (!isApiRequest(req.url, apiBaseUrl)) {
    return next(req);
  }

  return next(req).pipe(
    catchError((error: unknown) => {
      if (isLoginRequiredError(error)) {
        authRedirect.redirectToLogin();
        return EMPTY;
      }

      if (!isUnauthorized(error)) {
        return throwError(() => error);
      }

      return auth.getAccessTokenSilently({ cacheMode: 'off' }).pipe(
        catchError(() => {
          authRedirect.redirectToLogin();
          return EMPTY;
        }),
        switchMap((token) => {
          if (!token) {
            authRedirect.redirectToLogin();
            return EMPTY;
          }

          return next(
            req.clone({
              headers: req.headers.set('Authorization', `Bearer ${token}`),
            })
          ).pipe(
            catchError((retryError: unknown) => {
              if (isUnauthorized(retryError)) {
                authRedirect.redirectToLogin();
                return EMPTY;
              }

              return throwError(() => retryError);
            })
          );
        })
      );
    })
  );
};

function isApiRequest(url: string, apiBaseUrl: string): boolean {
  const base = apiBaseUrl.replace(/\/+$/, '');
  return (
    url === base || url.startsWith(`${base}/`) || url.startsWith(`${base}?`)
  );
}

function isUnauthorized(error: unknown): error is HttpErrorResponse {
  return error instanceof HttpErrorResponse && error.status === 401;
}

function isLoginRequiredError(error: unknown): boolean {
  if (typeof error !== 'object' || error === null || !('error' in error)) {
    return false;
  }

  const code = (error as { error?: unknown }).error;
  return typeof code === 'string' && LOGIN_REQUIRED_ERRORS.has(code);
}
